import { useCallback, useState } from 'react';

const STORAGE_KEY = 'trulyinbox-command-palette-recent';
const MAX_RECENT = 5;

/**
 * Per-viewer convenience only — never shared state, never read back by
 * anything but this browser. Every access is wrapped in try/catch: private
 * browsing, a full quota, or a blocked site can all throw on `localStorage`.
 */
function readRecentPageIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeRecentPageIds(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    // Best-effort — a failed write just means "no recent list next time".
  }
}

/**
 * The last few distinct pages visited via the command palette (most recent
 * first), shown in its default empty-query view. Recording a visit is
 * idempotent on order — re-visiting a page moves it to the front rather than
 * duplicating it.
 */
export function useRecentPages(): {
  recentIds: string[];
  recordVisit: (id: string) => void;
} {
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecentPageIds());

  const recordVisit = useCallback((id: string) => {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((existing) => existing !== id)].slice(0, MAX_RECENT);
      writeRecentPageIds(next);
      return next;
    });
  }, []);

  return { recentIds, recordVisit };
}
